import * as React from 'react';
import SurveyService, { GetResponsesParams } from '../services/SurveyService';
import qs from 'query-string';
import { selectActiveProjectId } from '../redux/modules/projectPicker/selectors';
import { useSelector } from 'react-redux';
import _isEqual from 'lodash/isEqual';
import useServiceAuth from './useServiceAuth';
import useToggle from './useToggle';
import { useCustomCompareEffect } from 'react-use';
import { defineMessages, useIntl } from 'react-intl';
import { useNotify } from './useNotify';
import { useActiveProjectCohortId } from './useActiveProjectCohortId';

const surveyService = new SurveyService();

export const messages = defineMessages({
  loadError: {
    id: 'useSurveyResponses.loadError',
    defaultMessage: 'Error occurred loading survey responses',
  },
});

export const useSurveyResponses = (
  responseParams: Omit<GetResponsesParams, 'projectId' | 'pageSize'>,
) => {
  const serviceAuth = useServiceAuth();
  const [isLoading, setLoading, setLoadingComplete] = useToggle(false);
  const [responses, setResponses] = React.useState<
    fhir.QuestionnaireResponse[]
  >([]);
  // Distinct from `!isLoading`, which is also true before the first fetch
  // starts and after a failed one, when `responses` is empty but unknown.
  const [hasFetchedResponses, setHasFetchedResponses] = React.useState(false);
  const projectId = useSelector(selectActiveProjectId);
  const [notify] = useNotify();
  const { formatMessage } = useIntl();
  const [error, setError] = React.useState<Error | null>(null);
  const cohort = useActiveProjectCohortId();

  const initializeResponseList = () => {
    if (isLoading || !projectId) return;

    async function loadAllResponses() {
      let nextPageToken = null;
      const rawResponses: fhir.QuestionnaireResponse[] = [];
      do {
        const params: any = {
          ...(responseParams || {}),
          projectId,
          cohort,
        };

        if (nextPageToken) {
          params.nextPageToken = nextPageToken;
        }

        const response = await surveyService.getResponses(serviceAuth)(params);
        rawResponses.push(...(response?.data?.items || []));
        nextPageToken = null;
        const nextLink = response.data?.links?.next;
        if (nextLink) {
          const queryParams = nextLink?.split('?')[1];
          // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
          nextPageToken = qs.parse(queryParams)?.nextPageToken || null;
        }
      } while (nextPageToken);

      return rawResponses;
    }

    setResponses([]);
    setHasFetchedResponses(false);
    setError(null);
    setLoading();
    loadAllResponses()
      .then(_ => {
        setResponses(_);
        setHasFetchedResponses(true);
      })
      .catch(e => {
        setError(e);
        notify(formatMessage(messages.loadError), 'error');
      })
      .finally(setLoadingComplete);
  };

  useCustomCompareEffect(
    () => {
      initializeResponseList();
    },
    [status, projectId, responseParams, cohort, initializeResponseList],
    (
      [prevStatus, prevProject, prevResponseParams, prevCohort],
      [status, project, responseParams, cohort],
    ) => {
      return (
        prevStatus === status &&
        prevProject === project &&
        prevCohort === cohort &&
        _isEqual(prevResponseParams, responseParams)
      );
    },
  );

  const updateResponse = React.useCallback(
    (response: fhir.QuestionnaireResponse) => {
      setResponses(responses =>
        responses.slice().map(r => (r.id === response.id ? response : r)),
      );
    },
    [],
  );

  return {
    isFetchingResponses: isLoading,
    hasFetchedResponses,
    fetchResponsesError: error,
    resetResponses: initializeResponseList,
    responses,
    updateResponse,
  };
};
